import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Camera, Loader2, Sparkles, X, ChevronRight, Share2, Download, Trophy, Send, Check, Plus } from "lucide-react";
import { resolveImageUrl } from "../lib/api";
import AdminReleaseForm from "./AdminReleaseForm";

// Client-side resize for mobile photos; keeps uploads fast.
function resizeImage(file, maxDim = 1280, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function VariantPicker({ variants, onAdd, addingId, onClose, navigate }) {
  return (
    <div data-testid="variant-picker" className="flex items-center gap-2 mt-2 flex-wrap">
      {variants.map((v) => {
        const isChase = v.is_chase;
        const isBlkltd = v.is_blkltd;
        const accent = isChase
          ? "bg-amber-500/15 border-amber-500/40 text-amber-400 hover:bg-amber-500/25"
          : isBlkltd
            ? "bg-purple-500/15 border-purple-500/40 text-purple-300 hover:bg-purple-500/25"
            : "bg-rose-600 border-rose-500 text-white hover:bg-rose-500";
        const ownedClass = v.owned ? "ring-1 ring-emerald-500/50" : "";
        return (
          <button
            key={v.release_id}
            type="button"
            data-testid={`variant-${v.variant_label.toLowerCase()}-${v.release_id}`}
            disabled={addingId === v.release_id}
            onClick={() => {
              if (v.owned) {
                onClose();
                navigate(`/catalog/${v.release_id}`);
              } else {
                onAdd(v);
              }
            }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${accent} ${ownedClass}`}
          >
            {addingId === v.release_id && <Loader2 className="w-3 h-3 animate-spin" />}
            <span>{v.variant_label}</span>
            {v.estimated_market_value != null && (
              <span className="text-[10px] opacity-80 font-mono">${Number(v.estimated_market_value).toFixed(0)}</span>
            )}
            {v.owned && <span className="text-[9px] uppercase tracking-wider">Owned</span>}
          </button>
        );
      })}
    </div>
  );
}

function CandidateCard({ release, onAdd, addingId, navigate, onClose }) {
  return (
    <div data-testid={`candidate-${release.release_id}`} className="p-3 rounded-xl bg-zinc-900/60 border border-zinc-800 hover:border-rose-500/40 transition-colors">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => { onClose(); navigate(`/catalog/${release.release_id}`); }}
          className="w-16 h-16 rounded-lg bg-zinc-950 overflow-hidden flex-shrink-0 border border-zinc-800"
        >
          {release.model_image ? (
            <img src={resolveImageUrl(release.model_image)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[9px] text-zinc-600 font-mono">{release.khmg_number}</div>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-100 truncate">{release.release_name}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-[10px] font-mono text-zinc-500">{release.khmg_number}</span>
            <span className="text-[10px] text-zinc-600">&middot; {release.vehicle}</span>
          </div>
          <p className="text-[10px] text-zinc-500 mt-1">
            Match confidence {Math.round(release.match_score * 100)}% {release.owned && <span className="text-emerald-400 font-medium">&middot; already owned</span>}
          </p>
        </div>
        {release.owned && (
          <Button
            data-testid={`view-owned-${release.release_id}`}
            variant="outline"
            size="sm"
            onClick={() => { onClose(); navigate(`/catalog/${release.release_id}`); }}
            className="bg-zinc-900 border-zinc-800 text-zinc-300 flex-shrink-0"
          >
            View <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        )}
      </div>
      {!release.owned && release.variants && release.variants.length > 1 ? (
        <div className="mt-2 pt-2 border-t border-zinc-800/50">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Pick variant</p>
          <VariantPicker
            variants={release.variants}
            onAdd={onAdd}
            addingId={addingId}
            onClose={onClose}
            navigate={navigate}
          />
        </div>
      ) : !release.owned ? (
        <div className="mt-2 flex justify-end">
          <Button
            data-testid={`add-match-${release.release_id}`}
            size="sm"
            onClick={() => onAdd(release)}
            disabled={addingId === release.release_id}
            className="bg-rose-600 hover:bg-rose-500 text-white"
          >
            {addingId === release.release_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Add to Collection
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default function QuickScanDialog({ onCollectionChange, trigger, onAddAsCustom, onCatalogChange }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  const [adding, setAdding] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [result, setResult] = useState(null);  // {identified, candidates}
  const [lastAdded, setLastAdded] = useState(null); // {release_id, release_name} for continuous mode
  const [sessionCount, setSessionCount] = useState(0);
  const [sessionItems, setSessionItems] = useState([]); // full records for the recap
  const [showRecap, setShowRecap] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postedHaulId, setPostedHaulId] = useState(null);
  const [adminFormOpen, setAdminFormOpen] = useState(false);
  const recapRef = useRef(null);
  const navigate = useNavigate();

  const reset = () => {
    setPreviewUrl(null);
    setResult(null);
    setLastAdded(null);
  };

  const resetForNextScan = () => {
    // Keep the dialog open; clear the preview/result so a fresh photo can be scanned.
    setPreviewUrl(null);
    setResult(null);
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      reset();
      setSessionCount(0);
      setSessionItems([]);
      setShowRecap(false);
      setPostedHaulId(null);
    }
  };

  const handlePostHaul = async () => {
    if (postedHaulId || posting) return;
    setPosting(true);
    try {
      const { data } = await api.post("/hauls", { items: sessionItems });
      setPostedHaulId(data.post_id);
      toast.success("Posted to your profile");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't post haul");
    } finally {
      setPosting(false);
    }
  };

  const handleDoneClick = () => {
    if (sessionCount > 0) {
      setShowRecap(true);
    } else {
      handleOpenChange(false);
    }
  };

  const sessionTotalValue = sessionItems.reduce(
    (sum, it) => sum + (Number(it.estimated_market_value) || 0),
    0
  );

  const handleShareRecap = async () => {
    const text = sessionCount === 1
      ? `Just added ${sessionItems[0]?.release_name || "a new model"} to my Kaido House collection.`
      : `Just added ${sessionCount} Kaido House models${sessionTotalValue > 0 ? ` worth $${sessionTotalValue.toFixed(0)}` : ""} to my collection.`;
    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
      } catch {
        toast.error("Couldn't copy");
      }
    };
    if (navigator.share) {
      try {
        await navigator.share({ title: "My Kaido House haul", text });
      } catch (e) {
        if (e.name === "AbortError") return; // user dismissed the share sheet
        await copyToClipboard();
      }
    } else {
      await copyToClipboard();
    }
  };

  const handleSaveImage = async () => {
    if (!recapRef.current) return;
    setSavingImage(true);
    try {
      const dataUrl = await toPng(recapRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#09090b",
        filter: (node) => !(node?.dataset?.recapExclude === "true"),
      });
      const link = document.createElement("a");
      link.download = `castfolio-haul-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Saved to downloads");
    } catch (err) {
      toast.error("Couldn't save image");
    } finally {
      setSavingImage(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use a JPEG, PNG, or WEBP image");
      return;
    }
    setIdentifying(true);
    setResult(null);
    setLastAdded(null);
    try {
      const dataUrl = await resizeImage(file);
      setPreviewUrl(dataUrl);
      toast.info("Scanning the catalog for a match...");
      const { data } = await api.post("/catalog/identify-and-match", { image_base64: dataUrl });
      setResult(data);
      if (!data.candidates?.length) {
        toast.warning("No catalog match. Try a different angle, or add as a custom item.");
      } else {
        toast.success(`Found ${data.candidates.length} possible match${data.candidates.length > 1 ? "es" : ""}.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      setIdentifying(false);
    }
  };

  const handleAdd = async (release) => {
    setAdding(release.release_id);
    try {
      await api.post("/collection", { release_id: release.release_id });
      toast.success(`Added ${release.release_name}`);
      onCollectionChange?.();
      // Continuous scan mode — keep the dialog open and queue for next scan.
      setLastAdded({ release_id: release.release_id, release_name: release.release_name });
      setSessionCount((n) => n + 1);
      setSessionItems((items) => [
        ...items,
        {
          release_id: release.release_id,
          release_name: release.release_name,
          khmg_number: release.khmg_number || "",
          model_image: release.model_image || null,
          variant_label: release.variant_label || null,
          estimated_market_value: Number(release.estimated_market_value) || 0,
        },
      ]);
      setPreviewUrl(null);
      setResult(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setAdding(null);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ? trigger : (
          <Button
            data-testid="quick-scan-btn"
            variant="outline"
            size="sm"
            className="bg-zinc-900 border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-rose-500/40"
          >
            <Camera className="w-3.5 h-3.5 mr-1" />Scan Photo
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="quick-scan-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-zinc-100 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-rose-400" />Scan & Match
            {sessionCount > 0 && (
              <span data-testid="scan-session-count" className="ml-auto text-[10px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5 uppercase tracking-wider">
                {sessionCount} added
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Snap a Kaido House model or its box. AI will find the catalog release and let you add it in one tap.</DialogDescription>
        </DialogHeader>

        {!showRecap && lastAdded && !result && !identifying && (
          <div data-testid="last-added-banner" className="mt-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-emerald-400/80 uppercase tracking-wider">Just added</p>
              <p className="text-sm text-zinc-100 truncate">{lastAdded.release_name}</p>
            </div>
            <Button
              data-testid="view-last-added-btn"
              variant="outline"
              size="sm"
              onClick={() => { handleOpenChange(false); navigate(`/catalog/${lastAdded.release_id}`); }}
              className="bg-zinc-900 border-zinc-800 text-zinc-300 flex-shrink-0"
            >
              View
            </Button>
            <Button
              data-testid="finish-scanning-btn"
              variant="ghost"
              size="sm"
              onClick={handleDoneClick}
              className="text-zinc-500 hover:text-zinc-200 flex-shrink-0"
            >
              Done
            </Button>
          </div>
        )}

        {!showRecap && !result && (
          <div className="mt-3 p-6 rounded-xl border-2 border-dashed border-zinc-800 bg-zinc-900/40 flex flex-col items-center justify-center gap-3">
            {previewUrl ? (
              <div className="relative w-32 h-32 rounded-xl overflow-hidden border border-rose-500/30">
                <img src={previewUrl} alt="preview" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={resetForNextScan}
                  data-testid="clear-scan-preview"
                  className="absolute -top-2 -right-2 bg-zinc-950 border border-zinc-700 rounded-full p-1 text-zinc-400 hover:text-rose-400"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <Camera className="w-10 h-10 text-zinc-700" />
            )}
            <label
              data-testid="quick-scan-file-btn"
              className={`cursor-pointer inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                identifying
                  ? "bg-zinc-800 text-zinc-500"
                  : "bg-rose-600 hover:bg-rose-500 text-white"
              }`}
            >
              {identifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              {identifying ? "Analyzing..." : previewUrl ? "Try another photo" : lastAdded ? "Scan another" : "Pick a photo"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                className="hidden"
                onChange={handleFile}
                disabled={identifying}
                data-testid="quick-scan-file-input"
              />
            </label>
            <p className="text-[11px] text-zinc-600">Works best with a clear shot of the packaging or the model itself</p>
          </div>
        )}

        {!showRecap && result && (
          <div className="mt-3 space-y-3">
            {previewUrl && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-900/40 border border-zinc-800">
                <img src={previewUrl} alt="" className="w-14 h-14 rounded-lg object-cover border border-zinc-800" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-0.5">AI identified</p>
                  <p className="text-sm text-zinc-200 truncate">
                    {[result.identified?.brand, result.identified?.vehicle, result.identified?.colorway].filter(Boolean).join(" · ") || "—"}
                  </p>
                  <p className="text-[10px] text-zinc-600 mt-0.5">Confidence: <span className="text-zinc-400">{result.identified?.confidence || "—"}</span></p>
                </div>
                <button
                  type="button"
                  onClick={resetForNextScan}
                  data-testid="retry-scan-btn"
                  className="text-[11px] text-rose-400 hover:text-rose-300 underline underline-offset-2 flex-shrink-0"
                >
                  Try again
                </button>
              </div>
            )}
            {result.candidates?.length > 0 ? (
              <div className="space-y-2">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Top {result.candidates.length} match{result.candidates.length > 1 ? "es" : ""}</p>
                {result.candidates.map((r) => (
                  <CandidateCard
                    key={r.release_id}
                    release={r}
                    onAdd={handleAdd}
                    addingId={adding}
                    navigate={navigate}
                    onClose={() => handleOpenChange(false)}
                  />
                ))}
                {adding && <p className="text-[11px] text-zinc-500 text-center"><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Adding...</p>}
              </div>
            ) : (
              <div data-testid="scan-no-match" className="text-center py-4 space-y-3">
                <p className="text-sm text-zinc-400">No catalog match found.</p>
                <p className="text-[11px] text-zinc-600">
                  {isAdmin
                    ? "Add it to the official catalog so it appears for everyone, or save it as a personal custom item."
                    : "This might be a non-Kaido House brand — keep your scan and add it as a custom item with the AI-identified fields pre-filled."}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {isAdmin && (
                    <Button
                      data-testid="scan-admin-add-catalog-btn"
                      onClick={() => setAdminFormOpen(true)}
                      size="sm"
                      className="bg-rose-600 hover:bg-rose-500 text-white"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1.5" /> Add to Catalog
                    </Button>
                  )}
                  {onAddAsCustom && (
                    <Button
                      data-testid="scan-add-as-custom-btn"
                      onClick={() => {
                        const id = result.identified || {};
                        // Map the AI shape to AddCustomItemDialog's form keys.
                        const prefill = {
                          ...(id.brand ? { brand: id.brand } : {}),
                          ...(id.release_name ? { release_name: id.release_name } : {}),
                          ...(id.vehicle ? { vehicle: id.vehicle } : {}),
                          ...(id.colorway ? { colorway: id.colorway } : {}),
                          ...(id.variant_type ? { variant_type: id.variant_type } : {}),
                          ...(id.is_chase ? { is_chase: true } : {}),
                          ...(id.is_blkltd ? { is_blkltd: true } : {}),
                          ...(id.rarity ? { rarity: id.rarity } : {}),
                          ...(id.msrp ? { msrp: String(id.msrp) } : {}),
                        };
                        const photo = previewUrl;
                        // Close the scan dialog first, then bubble up so the
                        // parent can mount AddCustomItemDialog with the
                        // captured data.
                        handleOpenChange(false);
                        onAddAsCustom(prefill, photo);
                      }}
                      size="sm"
                      variant={isAdmin ? "outline" : "default"}
                      className={isAdmin
                        ? "bg-zinc-900 border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-rose-500/40"
                        : "bg-rose-600 hover:bg-rose-500 text-white"}
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Add as Custom Item
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {showRecap && (
          <div data-testid="scan-recap" className="mt-3 space-y-3">
            <div
              ref={recapRef}
              className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-zinc-900 via-zinc-900 to-rose-950/30 border border-rose-500/20"
            >
              <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 10%, #f43f5e 0, transparent 40%), radial-gradient(circle at 80% 80%, #f59e0b 0, transparent 35%)" }} />
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-4 h-4 text-amber-400" />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-amber-400/80 font-mono">Scan session recap</p>
                </div>
                <div className="flex items-baseline gap-3 mb-1">
                  <span data-testid="recap-count" className="font-heading text-4xl text-zinc-100 leading-none">{sessionCount}</span>
                  <span className="text-sm text-zinc-400">item{sessionCount === 1 ? "" : "s"} added</span>
                </div>
                {sessionTotalValue > 0 && (
                  <p className="text-sm text-zinc-500">
                    Estimated value{" "}
                    <span data-testid="recap-total-value" className="text-emerald-400 font-mono font-semibold">
                      ${sessionTotalValue.toFixed(sessionTotalValue >= 100 ? 0 : 2)}
                    </span>
                  </p>
                )}
                <div className="mt-4 space-y-1.5 max-h-60 overflow-auto">
                  {sessionItems.map((it, idx) => (
                    <div key={`${it.release_id}-${idx}`} data-testid={`recap-item-${idx}`} className="flex items-center gap-3 p-2 rounded-lg bg-zinc-950/50 border border-zinc-800/70">
                      <div className="w-10 h-10 rounded bg-zinc-900 overflow-hidden flex-shrink-0 border border-zinc-800">
                        {it.model_image ? (
                          <img src={resolveImageUrl(it.model_image)} alt="" className="w-full h-full object-cover" crossOrigin="anonymous" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[8px] text-zinc-600 font-mono">{it.khmg_number || "—"}</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-200 truncate">{it.release_name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {it.variant_label && (
                            <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${
                              it.variant_label === "Chase" ? "bg-amber-500/15 text-amber-400" :
                              it.variant_label === "BLKLTD" ? "bg-purple-500/15 text-purple-300" :
                              "bg-rose-500/15 text-rose-400"
                            }`}>{it.variant_label}</span>
                          )}
                          {it.khmg_number && <span className="text-[9px] font-mono text-zinc-600">{it.khmg_number}</span>}
                        </div>
                      </div>
                      {it.estimated_market_value > 0 && (
                        <span className="text-xs font-mono text-emerald-400/80 flex-shrink-0">${Number(it.estimated_market_value).toFixed(0)}</span>
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-[10px] text-zinc-600 font-mono tracking-wider uppercase">Castfolio · {new Date().toLocaleDateString()}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                data-testid="recap-post-btn"
                onClick={handlePostHaul}
                disabled={posting || !!postedHaulId}
                size="sm"
                className={postedHaulId
                  ? "bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/20"
                  : "bg-rose-600 hover:bg-rose-500 text-white"
                }
              >
                {posting ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : postedHaulId ? (
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                ) : (
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                )}
                {postedHaulId ? "Posted to profile" : "Post to profile"}
              </Button>
              <Button
                data-testid="recap-share-btn"
                onClick={handleShareRecap}
                variant="outline"
                size="sm"
                className="bg-zinc-900 border-zinc-800 text-zinc-200 hover:border-rose-500/40"
              >
                <Share2 className="w-3.5 h-3.5 mr-1.5" />Share
              </Button>
              <Button
                data-testid="recap-save-btn"
                onClick={handleSaveImage}
                disabled={savingImage}
                variant="outline"
                size="sm"
                className="bg-zinc-900 border-zinc-800 text-zinc-200 hover:border-rose-500/40"
              >
                {savingImage ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                Save as image
              </Button>
              <div className="ml-auto flex items-center gap-2">
                <Button
                  data-testid="recap-scan-more-btn"
                  onClick={() => setShowRecap(false)}
                  variant="ghost"
                  size="sm"
                  className="text-zinc-400 hover:text-zinc-100"
                >
                  Scan more
                </Button>
                <Button
                  data-testid="recap-close-btn"
                  onClick={() => handleOpenChange(false)}
                  size="sm"
                  className="bg-rose-600 hover:bg-rose-500 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Controlled AdminReleaseForm — admin-only path that opens with AI
        fields pre-filled when no catalog candidate matched the scan. */}
    {isAdmin && (
      <AdminReleaseForm
        open={adminFormOpen}
        onOpenChange={setAdminFormOpen}
        prefill={(() => {
          const id = result?.identified || {};
          return {
            ...(id.brand ? { brand: id.brand } : {}),
            ...(id.release_name ? { release_name: id.release_name } : {}),
            ...(id.vehicle ? { vehicle: id.vehicle } : {}),
            ...(id.colorway ? { colorway: id.colorway } : {}),
            ...(id.variant_type ? { variant_type: id.variant_type } : {}),
            ...(id.is_chase ? { is_chase: true } : {}),
            ...(id.is_blkltd ? { is_blkltd: true } : {}),
            ...(id.rarity ? { rarity: id.rarity } : {}),
            ...(id.msrp ? { msrp: String(id.msrp) } : {}),
          };
        })()}
        onSuccess={() => {
          onCatalogChange?.();
          handleOpenChange(false);
          toast.success("Added to catalog!");
        }}
      />
    )}
    </>
  );
}
